import * as Sentry from '@sentry/angular';
import { Environment } from '@/environments/types';
import packageJson from '../../package.json';

/**
 * Beta-standaarden voor sampling; per omgeving te overrulen in env.js (runtime-config),
 * zodat quota bijgesteld kan worden zonder rebuild.
 */
const DEFAULT_TRACES_SAMPLE_RATE = 1;
const DEFAULT_REPLAYS_SESSION_SAMPLE_RATE = 0.1;
const DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE = 1;

/**
 * Sentry-initialisatie met strikte PII-scrubbing. Het platform verwerkt geboortedata;
 * die (en tokens, e-mailadressen, payloads) mogen nooit in een errortracker belanden.
 * Een lege/afwezige DSN in de runtime-config (env.js) betekent: Sentry volledig uit —
 * de standaard voor lokale ontwikkeling.
 *
 * Tracing en session replay staan aan voor de beta: fouten alleen zijn te dun om
 * moeilijk reproduceerbare bugs mee terug te vinden. De prijs daarvan is dat er meer
 * context meegaat, dus staat de maskering navenant streng (zie replayIntegration en
 * de beforeSend*-hooks): alle tekst en invoervelden gemaskeerd, geen media, geen
 * request-/response-bodies, en overal querystrings gestript.
 */
export function initSentry(env: Environment.Config): void {
  const sentry = env.sentry;
  if (!isSentryEnabled(sentry, window.location.hostname)) {
    return;
  }

  Sentry.init({
    dsn: sentry.dsn,
    environment: env.name,
    release: `mechanics-in-motion-frontend@${packageJson.version}`,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Replay legt schermdata vast; zonder deze maskering zouden geboortegegevens
        // letterlijk in beeld komen. Alles gemaskeerd, expliciet i.p.v. op default.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        // Geen request-/response-bodies of headers in de replay-netwerkdetails.
        networkDetailAllowUrls: []
      })
    ],
    tracesSampleRate: sentry.tracesSampleRate ?? DEFAULT_TRACES_SAMPLE_RATE,
    // Trace-headers alleen naar onze eigen (same-origin) API, niet naar derden.
    tracePropagationTargets: [/^\/(?!\/)/],
    replaysSessionSampleRate: sentry.replaysSessionSampleRate ?? DEFAULT_REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: sentry.replaysOnErrorSampleRate ?? DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE,
    sendDefaultPii: false,
    maxBreadcrumbs: 30,
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = scrubQueryStrings(event.request.url);
      }
      // Events blijven anoniem: geen gebruikersidentificatie in de tracker.
      delete event.user;
      return event;
    },
    beforeSendTransaction(event) {
      if (event.request?.url) {
        event.request.url = scrubQueryStrings(event.request.url);
      }
      delete event.user;
      if (event.transaction) {
        event.transaction = scrubQueryStrings(event.transaction);
      }
      // Span-beschrijvingen en -attributen bevatten de volledige request-URL, en juist
      // daar staan de geboortegegevens in: /api/ephemeris/positions?atUtc=...&latitude=...
      for (const span of event.spans ?? []) {
        if (span.description) {
          span.description = scrubQueryStrings(span.description);
        }
        const data = span.data;
        if (data) {
          for (const [key, value] of Object.entries(data)) {
            if (typeof value === 'string') {
              data[key] = scrubQueryStrings(value);
            }
          }
        }
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      // Console-breadcrumbs kunnen gelogde payloads bevatten; navigatie- en
      // netwerk-breadcrumbs (zonder querystring) volstaan voor diagnose.
      if (breadcrumb.category === 'console') {
        return null;
      }
      if (breadcrumb.data) {
        for (const key of ['url', 'to', 'from']) {
          const value = breadcrumb.data[key];
          if (typeof value === 'string') {
            breadcrumb.data[key] = scrubQueryStrings(value);
          }
        }
      }
      return breadcrumb;
    }
  });
}

/**
 * BELANGRIJK: de Docker-image draagt env.production.js, dus zonder `hosts` meldde ook de
 * lokale UAT-stack (localhost:8080) zich als 'prod'. Op 2026-10-29 leek Sentry zo vol
 * productie-500's te staan, terwijl ze allemaal uit een UAT-sessie kwamen. Met `hosts`
 * meldt alleen de echte productiehost zich. Zie sentry.spec.ts.
 */
export function isSentryEnabled(
  sentry: Environment.Sentry | undefined,
  hostname: string
): sentry is Environment.Sentry {
  if (!sentry?.dsn) {
    return false;
  }
  return !sentry.hosts?.length || sentry.hosts.includes(hostname);
}

/**
 * Elke querystring verdwijnt: die kan resettokens (?token=...) of geboortegegevens
 * (?atUtc=...&latitude=...) bevatten en het pad alleen is genoeg om te zien waar het
 * misging. Werkt ook op tekst waar een URL in verwerkt zit, zoals een span-beschrijving
 * ('GET /api/... ') of een Angular-foutboodschap.
 */
function scrubQueryStrings(text: string): string {
  return text.replace(/\?[^\s'"]*/g, '?[scrubbed]');
}
